import { generateDocumentId, generateLineageEdgeId } from "@/utils/ksuidGenerator";
import {
  documentService,
  type Document,
  type LineageEdge,
} from "@/services/documentService";
import { useProjectDocumentStore } from "@/stores/useProjectDocumentStore";
import { isAllowedChatFile, resolveChatFileMimeType } from "@/constants/allowedFileTypes";

const MAX_FILE_BYTES = 10 * 1024 * 1024;

export type UploadedProjectDocument = {
  publicId: string;
  name: string;
  contentType: string;
};

export type UploadProjectDocumentsResult = {
  uploaded: UploadedProjectDocument[];
  failed: Array<{ name: string; error: unknown }>;
};

function projectLineageEdges(documentId: string, projectId: string): LineageEdge[] {
  return [
    {
      id: generateLineageEdgeId(),
      documentId,
      entityType: "project",
      entityPublicId: projectId,
    },
  ];
}

/**
 * Uploads files as parallel single-file POSTs with project lineage edges.
 * Rejects disallowed types / oversized files into {@code failed} without aborting siblings.
 */
export async function uploadProjectDocumentsParallel(
  projectId: string,
  files: File[],
  onFileProgress?: (fileName: string, progress: number) => void,
): Promise<UploadProjectDocumentsResult> {
  const results = await Promise.allSettled(
    files.map(async (file): Promise<UploadedProjectDocument> => {
      if (!isAllowedChatFile(file)) {
        throw new Error(`Unsupported file type: ${file.name}`);
      }
      if (file.size > MAX_FILE_BYTES) {
        throw new Error(`File too large (max 10MB): ${file.name}`);
      }

      const contentType = resolveChatFileMimeType(file);
      const id = generateDocumentId();
      const lineageEdges = projectLineageEdges(id, projectId);
      const doc: Document = await documentService.uploadDocumentWithProgress(
        projectId,
        file,
        id,
        lineageEdges,
        (progress) => onFileProgress?.(file.name, progress),
      );
      const withEdges: Document = {
        ...doc,
        lineageEdges: doc.lineageEdges?.length ? doc.lineageEdges : lineageEdges,
      };
      useProjectDocumentStore.getState().upsertProjectDocument(projectId, withEdges);
      return {
        publicId: doc.id,
        name: file.name,
        contentType: contentType || doc.documentType || "application/octet-stream",
      };
    }),
  );

  const uploaded: UploadedProjectDocument[] = [];
  const failed: Array<{ name: string; error: unknown }> = [];

  results.forEach((result, index) => {
    if (result.status === "fulfilled") {
      uploaded.push(result.value);
      return;
    }
    failed.push({ name: files[index]?.name ?? "file", error: result.reason });
  });

  return { uploaded, failed };
}
